import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Thermometer, Droplets, Zap, Brain, Wifi, AlertTriangle, CheckCircle } from 'lucide-react';
import GlassCard from '../components/UI/GlassCard';
import FloatingElement from '../components/UI/FloatingElement';

interface SensorData {
  temperature: number;
  humidity: number;
  pH: number;
  timestamp: number;
  deviceId: string;
}

interface DeviceStatus {
  id: string;
  name: string;
  type: 'oven' | 'fridge' | 'scale' | 'thermometer';
  status: 'online' | 'offline' | 'warning';
  lastUpdate: number;
  battery?: number;
}

const SmartKitchen: React.FC = () => {
  const [sensorData, setSensorData] = useState<SensorData[]>([]);
  const [devices] = useState<DeviceStatus[]>([
    { id: 'oven_001', name: 'Smart Oven', type: 'oven', status: 'online', lastUpdate: Date.now(), battery: 85 },
    { id: 'fridge_001', name: 'Smart Fridge', type: 'fridge', status: 'online', lastUpdate: Date.now() },
    { id: 'scale_001', name: 'Kitchen Scale', type: 'scale', status: 'warning', lastUpdate: Date.now() - 300000, battery: 15 },
    { id: 'therm_001', name: 'Meat Thermometer', type: 'thermometer', status: 'offline', lastUpdate: Date.now() - 600000, battery: 0 }
  ]);
  const [aiSuggestion, setAiSuggestion] = useState<string>('');
  const [isConnected, setIsConnected] = useState(false);
  const [currentRecipe] = useState<string>('Chocolate Chip Cookies');

  // Simulate real-time sensor data
  useEffect(() => {
    const interval = setInterval(() => {
      const newData: SensorData = {
        temperature: 180 + Math.random() * 20 - 10, // 170-190°C
        humidity: 45 + Math.random() * 10 - 5, // 40-50%
        pH: 6.5 + Math.random() * 1 - 0.5, // 6.0-7.0
        timestamp: Date.now(),
        deviceId: 'oven_001'
      };
      
      setSensorData(prev => [...prev.slice(-19), newData]); // Keep last 20 readings
    }, 2000);

    // Simulate WebSocket connection
    setTimeout(() => setIsConnected(true), 1000);

    return () => clearInterval(interval);
  }, []);

  // AI suggestion based on sensor data
  useEffect(() => {
    if (sensorData.length > 0) {
      const latest = sensorData[sensorData.length - 1];
      generateAISuggestion(latest);
    }
  }, [sensorData]);

  const generateAISuggestion = async (data: SensorData) => {
    // Simulate AI processing
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    let suggestion = '';
    if (data.temperature > 185) {
      suggestion = '🔥 Temperature is high! Consider reducing heat to prevent burning. Your cookies should bake at 175°C for optimal texture.';
    } else if (data.temperature < 175) {
      suggestion = '❄️ Temperature is low. Increase heat slightly for proper baking. Cookies need consistent 180°C for golden browning.';
    } else if (data.humidity > 50) {
      suggestion = '💧 High humidity detected. This might affect baking time. Consider extending bake time by 2-3 minutes.';
    } else {
      suggestion = '✅ Perfect conditions! Your cookies are baking at optimal temperature and humidity. Expected completion in 8-10 minutes.';
    }
    
    setAiSuggestion(suggestion);
  };

  const getDeviceIcon = (type: string) => {
    switch (type) {
      case 'oven': return '🔥';
      case 'fridge': return '❄️';
      case 'scale': return '⚖️';
      case 'thermometer': return '🌡️';
      default: return '📱';
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'online': return 'text-green-400';
      case 'warning': return 'text-yellow-400';
      case 'offline': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'online': return <CheckCircle className="w-4 h-4" />;
      case 'warning': return <AlertTriangle className="w-4 h-4" />;
      case 'offline': return <Wifi className="w-4 h-4" />;
      default: return <Wifi className="w-4 h-4" />;
    }
  };

  const latestData = sensorData[sensorData.length - 1];

  return (
    <div className="min-h-screen px-4 sm:px-6 lg:px-8 py-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-12"
        >
          <FloatingElement delay={0}>
            <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
              <Brain className="w-10 h-10 text-white" />
            </div>
          </FloatingElement>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-4">
            Smart Kitchen <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
              Companion
            </span>
          </h1>
          <p className="text-xl text-purple-200 max-w-3xl mx-auto">
            Real-time IoT monitoring with AI-powered cooking insights. 
            Your intelligent kitchen assistant for perfect results every time.
          </p>
        </motion.div>

        {/* Connection Status */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.1 }}
          className="mb-8"
        >
          <GlassCard className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className={`w-3 h-3 rounded-full ${isConnected ? 'bg-green-400 animate-pulse' : 'bg-red-400'}`} />
                <span className="text-white font-medium">
                  {isConnected ? 'Connected to Smart Kitchen Network' : 'Connecting...'}
                </span>
              </div>
              <div className="text-purple-300 text-sm">
                Currently cooking: {currentRecipe}
              </div>
            </div>
          </GlassCard>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Real-time Sensor Data */}
          <div className="lg:col-span-2 space-y-6">
            {/* Live Metrics */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <GlassCard className="p-6">
                <h2 className="text-xl font-bold text-white mb-6 flex items-center">
                  <Zap className="w-6 h-6 text-yellow-400 mr-2" />
                  Live Sensor Data
                </h2>
                
                {latestData ? (
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-r from-red-500 to-orange-500 rounded-full flex items-center justify-center">
                        <Thermometer className="w-8 h-8 text-white" />
                      </div>
                      <div className="text-3xl font-bold text-white mb-1">
                        {latestData.temperature.toFixed(1)}°C
                      </div>
                      <div className="text-purple-300 text-sm">Temperature</div>
                      <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
                        <div 
                          className="h-2 rounded-full bg-gradient-to-r from-red-500 to-orange-500"
                          style={{ width: `${Math.min((latestData.temperature / 200) * 100, 100)}%` }}
                        />
                      </div>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                        <Droplets className="w-8 h-8 text-white" />
                      </div>
                      <div className="text-3xl font-bold text-white mb-1">
                        {latestData.humidity.toFixed(1)}%
                      </div>
                      <div className="text-purple-300 text-sm">Humidity</div>
                      <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
                        <div 
                          className="h-2 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500"
                          style={{ width: `${latestData.humidity}%` }}
                        />
                      </div>
                    </div>
                    
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto mb-3 bg-gradient-to-r from-green-500 to-teal-500 rounded-full flex items-center justify-center">
                        <span className="text-white font-bold text-lg">pH</span>
                      </div>
                      <div className="text-3xl font-bold text-white mb-1">
                        {latestData.pH.toFixed(1)}
                      </div>
                      <div className="text-purple-300 text-sm">Acidity Level</div>
                      <div className="w-full bg-gray-700 rounded-full h-2 mt-2">
                        <div 
                          className="h-2 rounded-full bg-gradient-to-r from-green-500 to-teal-500"
                          style={{ width: `${((latestData.pH - 6) / 2) * 100}%` }}
                        />
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500 mx-auto mb-4"></div>
                    <p className="text-purple-300">Waiting for sensor data...</p>
                  </div>
                )}
              </GlassCard>
            </motion.div>

            {/* AI Cooking Assistant */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.3 }}
            >
              <GlassCard className="p-6" glow>
                <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                  <Brain className="w-6 h-6 text-purple-400 mr-2" />
                  AI Cooking Assistant
                </h2>
                
                {aiSuggestion ? (
                  <div className="bg-gradient-to-r from-purple-500/20 to-pink-500/20 rounded-xl p-4 border border-purple-500/30">
                    <p className="text-white leading-relaxed">{aiSuggestion}</p>
                    <div className="flex items-center justify-between mt-4">
                      <span className="text-purple-300 text-sm">
                        Last updated: {new Date().toLocaleTimeString()}
                      </span>
                      <div className="flex items-center space-x-1">
                        <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                        <span className="text-green-400 text-sm">AI Active</span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-4">
                    <div className="animate-pulse">
                      <Brain className="w-8 h-8 text-purple-400 mx-auto mb-2" />
                      <p className="text-purple-300">AI is analyzing your cooking conditions...</p>
                    </div>
                  </div>
                )}
              </GlassCard>
            </motion.div>

            {/* Sensor History Chart */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              <GlassCard className="p-6">
                <h2 className="text-xl font-bold text-white mb-4">Temperature Trend</h2>
                <div className="h-32 flex items-end space-x-1">
                  {sensorData.slice(-20).map((data, index) => (
                    <div
                      key={index}
                      className="flex-1 bg-gradient-to-t from-red-500 to-orange-500 rounded-t opacity-70 hover:opacity-100 transition-opacity"
                      style={{ height: `${(data.temperature / 200) * 100}%` }}
                      title={`${data.temperature.toFixed(1)}°C at ${new Date(data.timestamp).toLocaleTimeString()}`}
                    />
                  ))}
                </div>
                <div className="flex justify-between text-purple-300 text-sm mt-2">
                  <span>20 readings ago</span>
                  <span>Now</span>
                </div>
              </GlassCard>
            </motion.div>
          </div>

          {/* Device Status Sidebar */}
          <div className="space-y-6">
            {/* Connected Devices */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.5 }}
            >
              <GlassCard className="p-6">
                <h2 className="text-xl font-bold text-white mb-4 flex items-center">
                  <Wifi className="w-6 h-6 text-cyan-400 mr-2" />
                  Connected Devices
                </h2>
                
                <div className="space-y-4">
                  {devices.map((device) => (
                    <div key={device.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{getDeviceIcon(device.type)}</span>
                        <div>
                          <div className="text-white font-medium">{device.name}</div>
                          <div className="text-purple-300 text-sm">
                            {new Date(device.lastUpdate).toLocaleTimeString()}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2">
                        {device.battery !== undefined && (
                          <div className="text-xs text-purple-300">
                            {device.battery}%
                          </div>
                        )}
                        <div className={getStatusColor(device.status)}>
                          {getStatusIcon(device.status)}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </GlassCard>
            </motion.div>

            {/* Quick Actions */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
            >
              <GlassCard className="p-6">
                <h2 className="text-xl font-bold text-white mb-4">Quick Actions</h2>
                
                <div className="space-y-3">
                  <button className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white py-3 rounded-lg font-medium hover:from-purple-600 hover:to-pink-600 transition-all duration-200">
                    Start New Recipe
                  </button>
                  
                  <button className="w-full bg-white/10 border border-white/20 text-white py-3 rounded-lg font-medium hover:bg-white/20 transition-all duration-200">
                    Set Timer
                  </button>
                  
                  <button className="w-full bg-white/10 border border-white/20 text-white py-3 rounded-lg font-medium hover:bg-white/20 transition-all duration-200">
                    Calibrate Sensors
                  </button>
                  
                  <button className="w-full bg-white/10 border border-white/20 text-white py-3 rounded-lg font-medium hover:bg-white/20 transition-all duration-200">
                    Export Data
                  </button>
                </div>
              </GlassCard>
            </motion.div>

            {/* Cooking Tips */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.7 }}
            >
              <GlassCard className="p-6">
                <h2 className="text-xl font-bold text-white mb-4">Smart Tips</h2>
                
                <div className="space-y-3 text-sm">
                  <div className="flex items-start space-x-2">
                    <span className="text-yellow-400">💡</span>
                    <span className="text-purple-200">
                      Optimal baking temperature for cookies is 175-180°C
                    </span>
                  </div>
                  
                  <div className="flex items-start space-x-2">
                    <span className="text-blue-400">💧</span>
                    <span className="text-purple-200">
                      High humidity may require 2-3 minutes extra baking time
                    </span>
                  </div>
                  
                  <div className="flex items-start space-x-2">
                    <span className="text-green-400">🎯</span>
                    <span className="text-purple-200">
                      pH levels between 6.0-7.0 are ideal for most doughs
                    </span>
                  </div>
                  
                  <div className="flex items-start space-x-2">
                    <span className="text-red-400">⚠️</span>
                    <span className="text-purple-200">
                      Temperature spikes above 190°C may cause burning
                    </span>
                  </div>
                </div>
              </GlassCard>
            </motion.div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmartKitchen;